import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { statusOf } from '../api/problem';
import { useDeletePasteMutation, useGetPasteQuery, type Paste } from '../api/generated';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import { ConfirmButton } from '../components/ConfirmButton';
import { CopyButton } from '../components/CopyButton';
import { NotFound } from '../components/NotFound';
import { describeStatus, isFinished } from '../components/executionText';
import { PasteEditor } from '../components/PasteEditor';
import { PasteTerminal } from '../components/PasteTerminal';
import { ProblemMessage } from '../components/ProblemMessage';
import { useExecution, type ExecutionState } from '../components/useExecution';
import { useLanguages } from '../components/useLanguages';
import { Workspace } from '../components/Workspace';
import { pasteForgotten, selectOwnedPaste } from '../features/owned/ownedPastesSlice';
import { terminalOpened } from '../features/ui/uiSlice';

const PASTE_ID = /^[0-9A-Za-z]{11}$/;

export function ViewPastePage() {
  const { id = '' } = useParams();
  if (!PASTE_ID.test(id)) {
    return <NotFound />;
  }
  return <ExistingPaste key={id} id={id} />;
}

function ExistingPaste({ id }: { readonly id: string }) {
  const { data: paste, error, isLoading } = useGetPasteQuery({ pasteId: id });

  if (isLoading) {
    return (
      <Workspace title="Paste">
        <article className="page" aria-busy="true">
          Loading paste…
        </article>
      </Workspace>
    );
  }
  if (statusOf(error) === 404) {
    return <NotFound />;
  }
  if (paste === undefined) {
    return (
      <Workspace title="Paste">
        <article className="page">
          <ProblemMessage error={error} />
        </article>
      </Workspace>
    );
  }
  return <LoadedPaste paste={paste} />;
}

function LoadedPaste({ paste }: { readonly paste: Paste }) {
  const languages = useLanguages();
  const checking = languages.isLoading || languages.loadFailed;
  const executable = languages.isExecutable(paste.language);
  const execution = useExecution(paste.id, executable);
  const [tab, setTab] = useState('stdout');
  const dispatch = useAppDispatch();

  // A build that fails while the user watches shows its output; one that failed earlier does not
  // pop up when the paste is opened.
  const status = execution.execution?.status;
  const previousStatus = useRef(status);
  useEffect(() => {
    if (status === 'build_failed' && previousStatus.current !== undefined && previousStatus.current !== status) {
      setTab('build');
      dispatch(terminalOpened());
    }
    previousStatus.current = status;
  }, [status, dispatch]);

  const run = async () => {
    setTab('stdout');
    dispatch(terminalOpened());
    await execution.run();
  };
  const idleMessage = execution.gone
    ? 'This paste and its execution are no longer available.'
    : checking
      ? 'Checking whether this paste can be run…'
    : executable
      ? 'Run the paste to see its output.'
      : `Pastes in ${languages.labelOf(paste.language)} cannot be run here.`;

  return (
    <Workspace
      title={paste.title ?? `Paste ${paste.id}`}
      options={<PasteDetails paste={paste} executable={executable} checking={checking} execution={execution} onRun={() => void run()} />}
      terminal={
        <PasteTerminal stdin={paste.stdin ?? ''} execution={execution.execution} idleMessage={idleMessage} activeTab={tab} onSelect={setTab} />
      }
    >
      <PasteEditor value={paste.content} language={paste.language} label="Paste content" readOnly />
    </Workspace>
  );
}

interface PasteDetailsProps {
  readonly paste: Paste;
  readonly executable: boolean;
  /** Whether the paste is executable is not known yet (languages loading or unavailable). */
  readonly checking: boolean;
  readonly execution: ExecutionState;
  readonly onRun: () => void;
}

function PasteDetails({ paste, executable, checking, execution, onRun }: PasteDetailsProps) {
  const owned = useAppSelector((state) => selectOwnedPaste(state, paste.id));
  const { labelOf } = useLanguages();
  const [deletePaste, deletion] = useDeletePasteMutation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const remove = async () => {
    const result = await deletePaste({ pasteId: paste.id });
    const status = statusOf(result.error);
    if (result.error === undefined || status === 403 || status === 404) {
      // consumed, rejected or already gone: the token is useless either way
      dispatch(pasteForgotten(paste.id));
    }
    if (result.error === undefined) {
      await navigate('/mine');
    }
  };

  return (
    <section aria-label="Paste details">
      <dl className="details">
        <dt>Language</dt>
        <dd>{labelOf(paste.language)}</dd>
        <dt>Created</dt>
        <dd>
          <time dateTime={paste.createdAt}>{new Date(paste.createdAt).toLocaleString()}</time>
        </dd>
        <dt>Size</dt>
        <dd>{paste.sizeBytes} bytes</dd>
        <dt>Execution</dt>
        <dd>{executionSummary(executable, checking, execution)}</dd>
      </dl>
      <div className="stack">
        {executable && (execution.notRun || execution.starting || (execution.execution !== undefined && !isFinished(execution.execution))) && (
          <button
            type="button"
            onClick={onRun}
            disabled={!execution.notRun || execution.starting}
            aria-busy={!execution.notRun || execution.starting}
            title="A paste runs once; everyone then sees the same result"
          >
            {execution.notRun && !execution.starting ? 'Run' : 'Running…'}
          </button>
        )}
        <ProblemMessage error={execution.error} />
        <CopyButton text={paste.content} label="Copy content" />
        <CopyButton text={globalThis.location.href} label="Copy link" />
        {owned !== undefined && (
          <ConfirmButton confirmLabel="Really delete" onConfirm={() => void remove()} busy={deletion.isLoading}>
            Delete paste
          </ConfirmButton>
        )}
      </div>
      <ProblemMessage error={deletion.error} />
    </section>
  );
}

function executionSummary(executable: boolean, checking: boolean, state: ExecutionState): string {
  if (checking) {
    return 'checking…';
  }
  if (state.gone) {
    return 'no longer available';
  }
  if (!executable) {
    return 'not available for this language';
  }
  if (state.execution !== undefined) {
    return describeStatus(state.execution);
  }
  return state.notRun ? 'not run yet' : 'checking…';
}
